import { Component, computed, input, output, signal, viewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { TranslocoPipe } from '@jsverse/transloco';
import { LvrButton } from '../../../../../core/components/lvr-button/lvr-button';
import { DatabaseColumnOption } from '../../models/database-column-selection.types';
import { areColumnSelectionsEqual } from '../../utils/database-column-selection';

@Component({
	selector: 'lvr-database-column-selector',
	templateUrl: './database-column-selector.html',
	styleUrl: './database-column-selector.scss',
	imports: [MatButton, MatCheckboxModule, MatIcon, MatMenuModule, TranslocoPipe, LvrButton],
})
export class DatabaseColumnSelector {
	public readonly options = input.required<readonly DatabaseColumnOption[]>();
	public readonly selectedColumnIds = input.required<readonly string[]>();
	public readonly applying = input(false);
	public readonly applySelection = output<readonly string[]>();
	public readonly cancelled = output<void>();

	private readonly menuTrigger = viewChild<MatMenuTrigger>('menuTrigger');
	private readonly pendingColumnIds = signal<string[]>([]);
	private closeAfterApply = false;

	public readonly selectedCount = computed(() => {
		const selected = new Set(this.selectedColumnIds());
		return this.options().filter((option) => selected.has(option.schemaId)).length;
	});

	public readonly pendingCount = computed(() => {
		const pending = new Set(this.pendingColumnIds());
		return this.options().filter((option) => pending.has(option.schemaId)).length;
	});

	public openMenu() {
		this.closeAfterApply = false;
		this.pendingColumnIds.set([...this.selectedColumnIds()]);
	}

	public isSelected(schemaId: string) {
		return this.pendingColumnIds().includes(schemaId);
	}

	public toggle(schemaId: string, checked: boolean) {
		if (this.applying()) return;
		this.pendingColumnIds.update((current) => {
			const next = current.filter((id) => id !== schemaId);
			return checked ? [...next, schemaId] : next;
		});
	}

	public selectAll() {
		if (this.applying()) return;
		this.pendingColumnIds.set(this.options().map((option) => option.schemaId));
	}

	public clearAll() {
		if (this.applying()) return;
		this.pendingColumnIds.set([]);
	}

	public canSelectAll() {
		return !this.applying() && this.pendingColumnIds().length < this.options().length;
	}

	public canClearAll() {
		return !this.applying() && this.pendingColumnIds().length > 0;
	}

	public canApply() {
		return (
			!this.applying() &&
			this.pendingColumnIds().length > 0 &&
			!areColumnSelectionsEqual(this.options(), this.pendingColumnIds(), this.selectedColumnIds())
		);
	}

	public apply() {
		const pending = this.pendingColumnIds();
		if (!this.canApply()) return;
		this.applySelection.emit([...pending]);
	}

	public close() {
		const trigger = this.menuTrigger();
		if (!trigger?.menuOpen) return;
		this.closeAfterApply = true;
		trigger.closeMenu();
	}

	public onMenuClosed() {
		if (this.closeAfterApply) {
			this.closeAfterApply = false;
			return;
		}

		// While applying, the list may be closed; selection stays locked by the disabled items.
		if (this.applying()) return;

		this.pendingColumnIds.set([...this.selectedColumnIds()]);
		this.cancelled.emit();
	}
}
